import { Head, usePage } from '@inertiajs/react'
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  Building2,
  Coins,
  FilePlus2,
  PiggyBank,
  Plus,
  Users,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import type { PageProps, UserRole } from '@/types/inertia'

type Metric = {
  label: string
  value: string
  hint: string
  icon: ComponentType<{ className?: string }>
}

const ROLE_LABEL: Record<UserRole, string> = {
  partner: 'Partner',
  lp: 'Limited Partner',
  founder: 'Founder',
}

function Stat({ metric }: { metric: Metric }) {
  const Icon = metric.icon
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {metric.label}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{metric.value}</div>
        <p className="text-xs text-muted-foreground">{metric.hint}</p>
      </CardContent>
    </Card>
  )
}

function PartnerDashboard() {
  const metrics: Metric[] = [
    { label: 'Active funds', value: '—', hint: 'No funds configured yet', icon: Briefcase },
    { label: 'Portfolio companies', value: '—', hint: 'No investments recorded', icon: Building2 },
    { label: 'Committed capital', value: '—', hint: 'Across all vintages', icon: Coins },
    { label: 'Limited partners', value: '—', hint: 'Across all funds', icon: Users },
  ]

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((m) => (
          <Stat key={m.label} metric={m} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Recent activity</CardTitle>
              <p className="text-sm text-muted-foreground">
                Investments, distributions and proceeds as they&apos;re recorded.
              </p>
            </div>
            <Button variant="ghost" size="sm" disabled>
              View all
              <ArrowRight />
            </Button>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            <div className="flex flex-col items-start gap-1 p-6">
              <p className="text-sm font-medium">Nothing to show yet.</p>
              <p className="text-sm text-muted-foreground">
                Once you record investments or proceeds, they&apos;ll appear here newest first.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quick actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button variant="secondary" className="justify-start" disabled>
                <Plus />
                New fund
              </Button>
              <Button variant="secondary" className="justify-start" disabled>
                <FilePlus2 />
                Record investment
              </Button>
              <Button variant="secondary" className="justify-start" disabled>
                <Users />
                Add limited partner
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>This week</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Capital calls</span>
                <Badge variant="outline">0 open</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Pending wires</span>
                <Badge variant="outline">0</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Reports due</span>
                <Badge variant="outline">0</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

function LpDashboard() {
  const metrics: Metric[] = [
    { label: 'Committed', value: '—', hint: 'Your aggregate commitments', icon: PiggyBank },
    { label: 'Called', value: '—', hint: 'Capital drawn to date', icon: Coins },
    { label: 'Distributions', value: '—', hint: 'Returned to you', icon: ArrowUpRight },
  ]

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        {metrics.map((m) => (
          <Stat key={m.label} metric={m} />
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your positions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-1">
          <p className="text-sm font-medium">No positions yet.</p>
          <p className="text-sm text-muted-foreground">
            Once you&apos;re added to a fund, your commitments and distributions will appear here.
          </p>
        </CardContent>
      </Card>
    </>
  )
}

function FounderDashboard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your company</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-1">
        <p className="text-sm font-medium">No company linked.</p>
        <p className="text-sm text-muted-foreground">
          Ask your partner contact to link you to your company&apos;s profile.
        </p>
      </CardContent>
    </Card>
  )
}

const DASHBOARDS: Record<UserRole, () => ReactNode> = {
  partner: PartnerDashboard,
  lp: LpDashboard,
  founder: FounderDashboard,
}

function Home() {
  const { auth } = usePage<PageProps>().props
  const user = auth.user
  if (!user) return null

  const Dashboard = DASHBOARDS[user.role] ?? PartnerDashboard
  const greeting = user.first_name || user.full_name || user.email

  return (
    <>
      <Head title="Dashboard" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Badge variant="secondary" className="mb-3">
              {ROLE_LABEL[user.role]}
            </Badge>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Hi, {greeting}.
            </h1>
            <p className="mt-2 text-muted-foreground">
              Here&apos;s what&apos;s happening across your workspace.
            </p>
          </div>
        </header>

        <div className="space-y-6">
          <Dashboard />
        </div>
      </div>
    </>
  )
}

export default Object.assign(Home, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
